import { FormControl, Grid, InputLabel, Select, TextField, Typography, Box, IconButton, InputAdornment } from '@mui/material';
import { Search as IconSearch } from "@mui/icons-material";
import api from "../services/api";
import * as personaService from "../services/personaService";
import * as filialService from "../services/filialService";
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { setEntity, setData, setItemValue, setEvaluacionDetalle, setValidationMessage, activeValidations
    , setListResponsables, setListParticipantes, setListTipos, setListEstados, setListDetalles, setListTemas } from "../redux/evaluacionDetalleSlice";
import { FormHelperText as HelperText } from '@mui/material';
import { AddCircle as IconAddCircle, Delete as IconDelete } from "@mui/icons-material";
import ParticipanteSearch from './ParticipanteSearch';
import TerapeutaSearch from './TerapeutaSearch';

// Mantenimiento de la evaluacion: la cabecera y el detalle de los temas
// evaluados. El detalle se guarda con la evaluacion.
const Evaluacion = (props) => {
    const isReadOnly = props.isReadOnly;
    const params = useParams();
    const idFilialUsuario = useSelector((state) => state.seguridad?.idFilial) || 0;
    const [listFiliales, setListFiliales] = useState([]);
    const entity = useSelector((state) => state.evaluacionDetalle.entity);
    const defaultEntity = useSelector((state) => state.evaluacionDetalle.defaultEntity);
    const listResponsables = useSelector((state) => state.evaluacionDetalle.listResponsables);
    const listParticipantes = useSelector((state) => state.evaluacionDetalle.listParticipantes);
    const listTipos = useSelector((state) => state.evaluacionDetalle.listTipos);
    const listEstados = useSelector((state) => state.evaluacionDetalle.listEstados);
    const listDetalles = useSelector((state) => state.evaluacionDetalle.listDetalles);
    const listTemas = useSelector((state) => state.evaluacionDetalle.listTemas);
    const validate = useSelector((state) => state.evaluacionDetalle.validationActive);
    const dispatch = useDispatch();

    // Ventanas de busqueda del participante y del responsable.
    const [openParticipanteSearch, setOpenParticipanteSearch] = useState(false);
    const [openTerapeutaSearch, setOpenTerapeutaSearch] = useState(false);

    var validation = {
        idParticipante: {
            error: entity.idParticipante == null || entity.idParticipante == 0,
            message: "Falta ingresar el participante",
        },
        fecha: {
            error: entity.fecha == null || entity.fecha === "",
            message: "Falta ingresar la fecha",
        },
        idResponsable: {
            error: entity.idResponsable == null || entity.idResponsable == 0,
            message: "Falta ingresar el responsable",
        },
        idTipo: {
            error: entity.idTipo == null || entity.idTipo == 0,
            message: "Falta ingresar el tipo",
        },
        idEstado: {
            error: entity.idEstado == null || entity.idEstado == 0,
            message: "Falta ingresar el estado",
        },
    }

    const fetchEntity = useCallback(async () => {
        if (parseInt(params.id || "0") <= 0) {
            dispatch(setEntity(defaultEntity));
            return;
        }

        await api.get("/Evaluacion/GetsEvaluacionView")
            .then((response) => {
                const encontrado = (response.data?.data || []).find((row) => row.id == params.id);

                if (encontrado == null) {
                    dispatch(setEntity(defaultEntity));
                }
                else {
                    dispatch(setEntity(encontrado));

                    // Los temas ya evaluados van en la entidad, que es lo que
                    // se guarda y lo que se dibuja.
                    cargarDetalle(params.id);
                }
            })
            .catch((err) => {
                console.log("Err: ", err);
            });
    }, [])

    // Los temas ya evaluados de esta evaluacion. Se cargan en las dos
    // listas: la de la entidad (se guarda) y la de consulta.
    const cargarDetalle = useCallback(async (idEvaluacion) => {
        if (idEvaluacion == null || idEvaluacion <= 0) {
            dispatch(setListDetalles([]));
            dispatch(setEvaluacionDetalle([]));
            return;
        }

        const response = await api.get("/Evaluacion/GetsEvaluacionDetalleView?idEvaluacion=" + idEvaluacion)
            .catch((err) => {
                console.log("Err: ", err)
            });

        // Se les asigna un numero para poder editarlos fila por fila.
        const filas = (response?.data?.data || []).map((row, indice) => ({ ...row, numero: indice + 1 }));

        dispatch(setListDetalles(filas));
        dispatch(setEvaluacionDetalle(filas));
    }, [])

    const fetchListResponsables = useCallback(async () => {
        const response = await personaService.getsListNaturalPersonByTypeAndName(25, "")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListResponsables(response || []));
    }, [])

    const fetchListParticipantes = useCallback(async () => {
        const response = await api.get("/Participante/GetsParticipantesResumenView")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListParticipantes(response?.data?.data || []));
    }, [])

    const fetchListTipos = useCallback(async () => {
        // Catalogo 311: tipos de evaluacion (Inicial, Seguimiento, Final...).
        const response = await api.get("/Catalogo/GetCatalogosByIdPadreInLista?idPadre=311")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListTipos(response?.data?.data || []));
    }, [])

    const fetchListEstados = useCallback(async () => {
        const response = await api.get("/Catalogo/GetCatalogosByIdPadreInLista?idPadre=1")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListEstados(response?.data?.data || []));
    }, [])

    // Los temas del catalogo, para elegir que se evalua. Los grupos (tema
    // padre) no se listan: solo los temas que se evaluan uno por uno.
    const fetchListTemas = useCallback(async () => {
        const response = await api.get("/Evaluacion/GetsTemaEvaluacionView")
            .catch((err) => {
                console.log("Err: ", err)
            });
        dispatch(setListTemas((response?.data?.data || []).filter((row) => row.idTemaPadre != null)));
    }, [])

    useEffect(() => {
        fetchEntity();
        fetchListResponsables();
        fetchListParticipantes();
        fetchListTipos();
        fetchListEstados();
        fetchListTemas();
        dispatch(setValidationMessage(messagesValidation()));
    }, []);

    useEffect(() => {
        dispatch(setValidationMessage(messagesValidation()));
    }, [entity])

    var setDataEntity = e => {
        dispatch(setData(e.target));
    }
    const messagesValidation = () => {
        var messages = [];

        Object.keys(validation).forEach(function (key) {
            if (validation[key].error) messages.push(validation[key].message)
        });

        return messages;
    }

    // El detalle: un campo de una fila de temas evaluados.
    const setItemValueDetalle = (numero, name, value) => {
        dispatch(setItemValue({ numero: numero, name: name, value: value }))
    }

    const agregarDetalle = () => {
        const maxNumero = (entity.evaluacionDetalle || []).length === 0 ? 0
            : Math.max.apply(null, (entity.evaluacionDetalle || []).map((item) => { return item.numero; }))

        dispatch(setEvaluacionDetalle([...(entity.evaluacionDetalle || []), {
            id: 0,
            numero: maxNumero + 1,
            idTema: 0,
            resultado: "",
            observaciones: "",
        }]))
    }

    const quitarDetalle = (numero) => {
        dispatch(setEvaluacionDetalle(
            (entity.evaluacionDetalle || []).filter((item) => item.numero !== numero)
        ))
    }

    return (
        <Grid container>
            <Grid item>
                <Box sx={{ fontWeight: 'bold' }} padding={0}>
                    <Typography variant="h7">
                        Datos Basicos
                    </Typography>
                </Box>
            </Grid>
            <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                {/* El participante se busca con la lupa: es una ventana
                    propia para no cargar la lista completa. */}
                <Grid item xs={6} sm={2.5}>
                    <FormControl required fullWidth>
                        <InputLabel htmlFor="input-filial">Filial</InputLabel>
                        <Select
                            native
                            label="Filial"
                            value={entity.idFilial || (idFilialUsuario > 0 ? idFilialUsuario : 0)}
                            onChange={(event) => dispatch(setData({ name: "idFilial", value: parseInt(event.target.value) || 0 }))}
                            inputProps={{ name: "idFilial", id: "input-filial" }}
                            disabled={isReadOnly || idFilialUsuario > 0}
                            size="small"
                        >
                            {idFilialUsuario <= 0 && <option key={0} value={0}>{"(Seleccione)"}</option>}
                            {listFiliales.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion || row.nombre}</option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <FormControl required fullWidth error={validate.idParticipante && validation.idParticipante.error}>
                        <TextField
                            label="Participante"
                            fullWidth
                            value={entity.participante}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="buscar participante"
                                            size="small"
                                            disabled={isReadOnly}
                                            onClick={() => setOpenParticipanteSearch(true)}>
                                            <IconSearch fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            InputLabelProps={{ shrink: true }}
                            disabled={isReadOnly}
                            size="small"
                        />
                        <HelperText>
                            {validate.idParticipante && validation.idParticipante.error ? validate.idParticipante.message : ""}
                        </HelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <FormControl required fullWidth error={validate.fecha && validation.fecha.error}>
                        <TextField
                            required
                            name="fecha"
                            label="Fecha"
                            type="date"
                            fullWidth
                            value={entity.fecha == null ? "" : String(entity.fecha).substring(0, 10)}
                            onChange={setDataEntity}
                            error={validate.fecha && validation.fecha.error}
                            helperText={validate.fecha && validation.fecha.error ? validation.fecha.message : ""}
                            InputLabelProps={{ shrink: true }}
                            disabled={isReadOnly}
                            size="small"
                        />
                    </FormControl>
                </Grid>
                {/* El responsable (terapeuta) tambien se busca con lupa. */}
                <Grid item xs={6} sm={3}>
                    <FormControl required fullWidth error={validate.idResponsable && validation.idResponsable.error}>
                        <TextField
                            label="Responsable"
                            fullWidth
                            value={entity.responsable}
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="buscar terapeuta"
                                            size="small"
                                            disabled={isReadOnly}
                                            onClick={() => setOpenTerapeutaSearch(true)}>
                                            <IconSearch fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            InputLabelProps={{ shrink: true }}
                            disabled={isReadOnly}
                            size="small"
                        />
                        <HelperText>
                            {validate.idResponsable && validation.idResponsable.error ? validation.idResponsable.message : ""}
                        </HelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <FormControl required fullWidth error={validate.idTipo && validation.idTipo.error}>
                        <InputLabel htmlFor="input-tipo">Tipo</InputLabel>
                        <Select
                            native
                            label="Tipo"
                            value={entity.idTipo}
                            onChange={setDataEntity}
                            inputProps={{
                                name: 'idTipo',
                                id: 'input-tipo',
                            }}
                            disabled={isReadOnly}
                            size="small"
                        >
                            <option key={0} value={0}>{"(Seleccione)"}</option>
                            {listTipos.map((row) => (
                                <option key={row.id} value={row.id}>{row.descripcion}</option>
                            ))}
                        </Select>
                        <HelperText>
                            {validate.idTipo && validation.idTipo.error ? validation.idTipo.message : ""}
                        </HelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <FormControl fullWidth>
                        <TextField
                            name="observaciones"
                            label="Observaciones"
                            fullWidth
                            value={entity.observaciones}
                            onChange={setDataEntity}
                            disabled={isReadOnly}
                            size="small"
                        />
                    </FormControl>
                </Grid>
            </Grid>

            {/* Temas evaluados */}
            <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                <Grid item xs={12} sm={12}>
                    <Box sx={{ fontWeight: 'bold' }} padding={0}>
                        <Typography variant="h7">
                            Temas evaluados
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <IconButton disabled={isReadOnly} size="small" aria-label="Agregar tema"
                        style={{ height: 10 }} onClick={agregarDetalle}>
                        <IconAddCircle />
                    </IconButton>
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                        {(entity.evaluacionDetalle || []).map((row) => (
                            <Box key={row.numero} sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1, padding: 1, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, marginBottom: 1 }}>
                                <Grid container spacing={1} padding={0}>
                                    <Grid item xs={12} sm={4}>
                                        <FormControl required fullWidth>
                                            <InputLabel htmlFor={"input-tema-" + row.numero}>Tema</InputLabel>
                                            <Select
                                                native
                                                label="Tema"
                                                value={row.idTema}
                                                onChange={(e) => {
                                                    setItemValueDetalle(row.numero, "idTema", e.target.value)
                                                }}
                                                inputProps={{
                                                    name: 'idTema',
                                                    id: 'input-tema-' + row.numero,
                                                }}
                                                disabled={isReadOnly}
                                                size="small"
                                            >
                                                <option key={0} value={0}>{"(Seleccione)"}</option>
                                                {listTemas.map((childrow) => (
                                                    <option key={childrow.id} value={childrow.id}>{childrow.nombre}</option>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            name="resultado"
                                            label="Resultado"
                                            fullWidth
                                            value={row.resultado}
                                            onChange={(e) => {
                                                setItemValueDetalle(row.numero, "resultado", e.target.value)
                                            }}
                                            disabled={isReadOnly}
                                            size="small"
                                        />
                                    </Grid>
                                    <Grid item xs={1} sm={1}>
                                        <IconButton aria-label="delete" disabled={isReadOnly} onClick={() => {
                                            quitarDetalle(row.numero);
                                        }}>
                                            <IconDelete />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <TextField
                                        name="observaciones"
                                        label="Observaciones"
                                        fullWidth
                                        value={row.observaciones}
                                        onChange={(e) => {
                                            setItemValueDetalle(row.numero, "observaciones", e.target.value)
                                        }}
                                        disabled={isReadOnly}
                                        size="small"
                                    />
                                </Grid>
                            </Box>
                        ))}
                    </Box>
                </Grid>
            </Grid>

            {/* Busqueda de participante y de terapeuta */}
            <ParticipanteSearch
                open={openParticipanteSearch}
                openClose={() => setOpenParticipanteSearch(false)}
                setSelection={(row) => {
                    dispatch(setData({ name: "idParticipante", value: row.id }))
                    dispatch(setData({ name: "participante", value: row.participante }))
                }}
            />
            <TerapeutaSearch
                open={openTerapeutaSearch}
                openClose={() => setOpenTerapeutaSearch(false)}
                setSelection={(row) => {
                    dispatch(setData({ name: "idResponsable", value: row.id }))
                    dispatch(setData({ name: "responsable", value: row.descripcion }))
                }}
            />
        </Grid>
    );
}

export default Evaluacion;